<template>
  <l-form autocomplete="off" @submit="submit">
    <a
      :href="route('schools.show', school.slug)"
      class="inline-block text-reset hover:text-current mb-6"
    >
      <h2 class="font-semibold text-blue dark:text-white text-2xl mb-1">
        {{ school.name }}
      </h2>

      <div class="text-gray-500 font-normal flex  items-center gap-6">
        <div class="flex gap-1 items-center whitespace-nowrap">
          <icon name="school" color="gray"/>
          {{ new SchoolType(school.type).label() }} school
        </div>
        <div class="flex gap-1 items-center whitespace-nowrap">
          <icon name="location" class="text-gray-300"/>
          {{ school.address }}
        </div>
      </div>
    </a>

    <div class="mb-8">
      <div class="flex items-center gap-2">
        <l-checkbox
          v-model="budget.part_time"
          name="part_time"
          value="1"
          label="Part-time"
        />
      </div>

      <p class="text-sm text-gray-500 leading-relaxed">
        To generate a budget and projected debt for attending law school part-time, use credit-based inputs from the
        school's website.
      </p>
    </div>

    <h3 class="mb-3 text-lg font-semibold text-blue dark:text-white">
      Cost of attendance:
      <span class="text-red">{{ number_format(totalCost) }}</span>
    </h3>

    <div class="overflow-x-auto">
      <table class="l-table min-w-[30rem] text-right mb-2">
        <thead>
          <tr class="font-semibold text-blue dark:text-white">
            <th>
              Expenses & borrowing limits <span
                class="block text-sm font-normal text-gray-500"
              >9 months per year</span>
            </th>
            <th :class="partTime ? 'w-[9rem]' : 'w-1/5'">
              1st year
            </th>
            <th :class="partTime ? 'w-[9rem]' : 'w-1/5'">
              2nd year
            </th>
            <th :class="partTime ? 'w-[9rem]' : 'w-1/5'">
              3rd year
            </th>
            <th v-if="partTime" class="w-36">
              4th year
            </th>
            <th v-if="partTime" class="w-36">
              5th year
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              Tuition & fees
              <div class="text-sm text-gray-500">
                {{ tuitionHelpText }}
              </div>
            </td>
            <td v-for="(field, index) in yearPrefixes.map((prefix) => prefix + 'tuition')" :key="field">
              <l-input
                v-model="budget[field]"
                class="label:sr-only"
                prefix="$"
                type="number"
                :name="field"
                :label="`Year ${index+1} tuition and fees`"
                :required="partTime || ['y1_tuition', 'y2_tuition', 'y3_tuition'].includes(field)"
              />
            </td>
          </tr>
          <tr>
            <td>Room & board</td>
            <td v-for="(field, index) in yearPrefixes.map((prefix) => prefix + 'room')" :key="field">
              <l-input
                v-model="budget[field]"
                class="label:sr-only"
                prefix="$"
                type="number"
                :name="field"
                :label="`Year ${index+1} room and board`"
              />
            </td>
          </tr>
          <tr>
            <td>Books &amp; supplies</td>
            <td v-for="(field, index) in yearPrefixes.map((prefix) => prefix + 'books')" :key="field">
              <l-input
                v-model="budget[field]"
                class="label:sr-only"
                prefix="$"
                type="number"
                :name="field"
                :label="`Year ${index+1} book and supplies`"
              />
            </td>
          </tr>
          <tr>
            <td>Transportation</td>
            <td v-for="(field, index) in yearPrefixes.map((prefix) => prefix + 'transportation')" :key="field">
              <l-input
                v-model="budget[field]"
                class="label:sr-only"
                prefix="$"
                type="number"
                :name="field"
                :label="`Year ${index+1} transportation`"
              />
            </td>
          </tr>
          <tr>
            <td>Other personal expenses</td>
            <td v-for="(field, index) in yearPrefixes.map((prefix) => prefix + 'other')" :key="field">
              <l-input
                v-model="budget[field]"
                class="label:sr-only"
                prefix="$"
                type="number"
                :name="field"
                :label="`Year ${index+1} personal expenses`"
              />
            </td>
          </tr>
          <tr>
            <td>Direct loan fee ({{ staffordFee }}%)</td>
            <td>{{ number_format(y1Fees.stafford) }}</td>
            <td>{{ number_format(y2Fees.stafford) }}</td>
            <td>{{ number_format(y3Fees.stafford) }}</td>
            <td v-if="partTime">
              {{ number_format(y4Fees.stafford) }}
            </td>
            <td v-if="partTime">
              {{ number_format(y5Fees.stafford) }}
            </td>
          </tr>
          <tr>
            <td>Direct PLUS loan fee ({{ gradPlusFee }}%)</td>
            <td>{{ number_format(y1Fees.gradplus) }}</td>
            <td>{{ number_format(y2Fees.gradplus) }}</td>
            <td>{{ number_format(y3Fees.gradplus) }}</td>
            <td v-if="partTime">
              {{ number_format(y4Fees.gradplus) }}
            </td>
            <td v-if="partTime">
              {{ number_format(y5Fees.gradplus) }}
            </td>
          </tr>
          <tr class="bg-yellow-100">
            <td>
              <span class="font-semibold text-blue dark:text-white">
                Total
              </span>
              <div class="text-sm text-gray-500">
                {{ colHelpText }}
              </div>
            </td>
            <td class="font-semibold text-blue dark:text-white">
              {{ number_format(y1CostWithFees) }}
            </td>
            <td class="font-semibold text-blue dark:text-white">
              {{ number_format(y2CostWithFees) }}
            </td>
            <td class="font-semibold text-blue dark:text-white">
              {{ number_format(y3CostWithFees) }}
            </td>
            <td v-if="partTime" class="font-semibold text-blue dark:text-white">
              {{ number_format(y4CostWithFees) }}
            </td>
            <td v-if="partTime" class="font-semibold text-blue dark:text-white">
              {{ number_format(y5CostWithFees) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <p class="mb-8 text-gray-500 text-sm">
      You may not be able to borrow to cover all of your estimated expenses. Borrowing limits are set by each law school.
    </p>

    <h3 class="mb-3 mt-6 text-lg font-semibold text-blue dark:text-white">
      Projected contribution:
      <span class="text-green">{{ number_format(totalContribution) }}</span>
    </h3>
    <div class="overflow-x-auto">
      <table class="l-table min-w-[30rem] text-right ">
        <thead>
          <tr class="font-semibold text-blue dark:text-white">
            <th>
              Variable financial assistance
            </th>
            <th :class="partTime ? 'w-[9rem]' : 'w-1/5'">
              1st year
            </th>
            <th :class="partTime ? 'w-[9rem]' : 'w-1/5'">
              2nd year
            </th>
            <th :class="partTime ? 'w-[9rem]' : 'w-1/5'">
              3rd year
            </th>
            <th v-if="partTime" class="w-36">
              4th year
            </th>
            <th v-if="partTime" class="w-36">
              5th year
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              Scholarship or grants<span class="block text-sm text-gray-500">from {{ school.name_limited }}</span>
            </td>
            <td v-for="(field, index) in yearPrefixes.map((prefix) => prefix + 'discount')" :key="field">
              <l-input
                v-model="budget[field]"
                class="label:sr-only"
                prefix="$"
                type="number"
                :name="field"
                :label="`Year ${index+1} scholarships or grants`"
              />
            </td>
          </tr>
          <tr>
            <td>
              Other assistance<span
                class="block text-sm text-gray-500"
              >private scholarships specific to {{ school.name_limited }}</span>
            </td>
            <td
              v-for="(field, index) in yearPrefixes.map((prefix) => prefix + 'contribution_private')"
              :key="field"
            >
              <l-input
                v-model="budget[field]"
                class="label:sr-only"
                prefix="$"
                type="number"
                :name="field"
                :label="`Year ${index+1} private scholarships`"
              />
            </td>
          </tr>
          <tr>
            <td>
              Personal or family contribution<span class="block text-sm text-gray-500">only for {{
                school.name_limited
              }}</span>
            </td>
            <td
              v-for="(field, index) in yearPrefixes.map((prefix) => prefix + 'contribution_specific')"
              :key="field"
            >
              <l-input
                v-model="budget[field]"
                class="label:sr-only"
                prefix="$"
                type="number"
                :name="field"
                :label="`Year ${index+1} personal contribution`"
              />
            </td>
          </tr>
          <tr>
            <td>
              <span class="inline-flex gap-2 justify-end">
                <span>Other contributions</span>
                <a
                  :href="route('settings.finances')"
                  class="bg-transparent text-sm hover:no-underline p-0"
                >
                  <icon name="edit" set="regular"/>
                </a>
              </span>
              <span class="block text-sm text-gray-500">for any school</span>
            </td>
            <td>{{ number_format(assumptions.y1_contribution || 0) }}</td>
            <td>{{ number_format(assumptions.y2_contribution || 0) }}</td>
            <td>{{ number_format(assumptions.y3_contribution || 0) }}</td>
            <td v-if="partTime">
              {{ number_format(assumptions.y4_contribution || 0) }}
            </td>
            <td v-if="partTime">
              {{ number_format(assumptions.y5_contribution || 0) }}
            </td>
          </tr>
          <tr class="bg-yellow-100 font-semibold text-blue dark:text-white">
            <td>
              Total
            </td>
            <td>{{ number_format(y1Contribution) }}</td>
            <td>{{ number_format(y2Contribution) }}</td>
            <td>{{ number_format(y3Contribution) }}</td>
            <td v-if="partTime">
              {{ number_format(y4Contribution) }}
            </td>
            <td v-if="partTime">
              {{ number_format(y5Contribution) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <slot/>

    <div class="flex flex-row-reverse gap-3 mt-10">
      <l-button type="submit" variant="primary" :loading="loading">
        {{ loading ? 'Saving' : 'Save budget' }}
      </l-button>

      <budget-delete-modal v-if="hasBudget" v-slot="{ activator }" :school="school">
        <l-button variant="light" @click="activator">
          Delete
        </l-button>
      </budget-delete-modal>
    </div>
  </l-form>
</template>

<script setup>
import BudgetDeleteModal from "@/components/modals/BudgetDeleteModal.vue"
import Icon from "@/components/Icon.vue"
import LButton from "@/components/buttons/LButton.vue"
import LCheckbox from "@/components/inputs/LCheckbox.vue"
import LForm from "@/components/forms/LForm.vue"
import LInput from "@/components/inputs/LInput.vue"
import route from "ziggy-js"
import { SchoolType } from "@/models/SchoolType"
import { api } from "@/api"
import { computed, ref } from "vue"
import { toast } from "@/notifications"

const budget = defineModel(Object)

const props = defineProps({
  staffordMax: { type: Number, required: true },
  staffordFee: { type: Number, required: true },
  gradPlusFee: { type: Number, required: true },
  school: { type: Object, required: true },
  assumptions: { type: Object },
  tuitionHelpText: { type: String, required: true },
  colHelpText: { type: String, required: true },
})

const loading = ref(false)

const hasBudget = computed(() => !!budget.value)
const partTime = computed(() => (budget.value ? budget.value.part_time : false))

const submit = () => {
  loading.value = true
  api.budgetCreate({
    ...budget.value,
    part_time: budget.value.part_time,
    school: props.school.id,
  }).then(() => {
    loading.value = false

    toast.success("You successfully saved this budget.")
  })
}

const y1Cost = computed(() => {
  return ((budget.value.y1_tuition || 0) + (budget.value.y1_room || 0) + (budget.value.y1_transportation) + (budget.value.y1_books || 0) + (budget.value.y1_other || 0))
})
const y1CostWithFees = computed(() => {
  return y1Cost.value + y1Fees.value.stafford + y1Fees.value.gradplus
})

const y2Cost = computed(() => {
  return ((budget.value.y2_tuition || 0) + (budget.value.y2_room || 0) + (budget.value.y2_transportation) + (budget.value.y2_books || 0) + (budget.value.y2_other || 0))
})
const y2CostWithFees = computed(() => {
  return y2Cost.value + y2Fees.value.stafford + y2Fees.value.gradplus
})

const y3Cost = computed(() => {
  return ((budget.value.y3_tuition || 0) + (budget.value.y3_room || 0) + (budget.value.y3_transportation) + (budget.value.y3_books || 0) + (budget.value.y3_other || 0))
})
const y3CostWithFees = computed(() => {
  return y3Cost.value + y3Fees.value.stafford + y3Fees.value.gradplus
})

const y4Cost = computed(() => {
  return ((budget.value.y4_tuition || 0) + (budget.value.y4_room || 0) + (budget.value.y4_transportation) + (budget.value.y4_books || 0) + (budget.value.y4_other || 0))
})
const y4CostWithFees = computed(() => {
  return y4Cost.value + y4Fees.value.stafford + y4Fees.value.gradplus
})

const y5Cost = computed(() => {
  return ((budget.value.y5_tuition || 0) + (budget.value.y5_room || 0) + (budget.value.y5_transportation) + (budget.value.y5_books || 0) + (budget.value.y5_other || 0))
})
const y5CostWithFees = computed(() => {
  return y5Cost.value + y5Fees.value.stafford + y5Fees.value.gradplus
})

const totalCost = computed(() => {
  let cost = y1CostWithFees.value + y2CostWithFees.value + y3CostWithFees.value

  if (partTime.value) {
    cost += y4CostWithFees.value + y5CostWithFees.value
  }

  return cost
})

const y1Contribution = computed(() => {
  return ((budget.value.y1_discount || 0) + (budget.value.y1_contribution_private || 0) + (budget.value.y1_contribution_specific || 0) + (props.assumptions ? props.assumptions.y1_contribution : 0))
})
const y2Contribution = computed(() => {
  return ((budget.value.y2_discount || 0) + (budget.value.y2_contribution_private || 0) + (budget.value.y2_contribution_specific || 0) + (props.assumptions ? props.assumptions.y2_contribution : 0))
})
const y3Contribution = computed(() => {
  return ((budget.value.y3_discount || 0) + (budget.value.y3_contribution_private || 0) + (budget.value.y3_contribution_specific || 0) + (props.assumptions ? props.assumptions.y3_contribution : 0))
})
const y4Contribution = computed(() => {
  return ((budget.value.y4_discount || 0) + (budget.value.y4_contribution_private || 0) + (budget.value.y4_contribution_specific || 0) + (props.assumptions ? props.assumptions.y4_contribution : 0))
})
const y5Contribution = computed(() => {
  return ((budget.value.y5_discount || 0) + (budget.value.y5_contribution_private || 0) + (budget.value.y5_contribution_specific || 0) + (props.assumptions ? props.assumptions.y5_contribution : 0))
})
const totalContribution = computed(() => {
  return (y1Contribution.value + y2Contribution.value + y3Contribution.value + y4Contribution.value + y5Contribution.value)
})

const y1Fees = computed(() => getFees(y1Cost.value, y1Contribution.value))
const y2Fees = computed(() => getFees(y2Cost.value, y2Contribution.value))
const y3Fees = computed(() => getFees(y3Cost.value, y3Contribution.value))
const y4Fees = computed(() => getFees(y4Cost.value, y4Contribution.value))
const y5Fees = computed(() => getFees(y5Cost.value, y5Contribution.value))

const getFees = (cost, contribution) => {
  const borrowing = cost - contribution
  let sFee = 0
  let gFee = 0

  if (borrowing <= 0) {
    sFee = 0
    gFee = 0
  } else if (borrowing <= props.staffordMax) {
    sFee = Math.round(borrowing * (props.staffordFee / 100))

    if ((borrowing + sFee) > props.staffordMax) {
      gFee = Math.round((borrowing + sFee * 1 - props.staffordMax) * (props.gradPlusFee / 100))
    } else {
      gFee = 0
    }
  } else {
    sFee = Math.round(props.staffordMax * (props.staffordFee / 100))
    gFee = Math.round((borrowing - props.staffordMax) * (props.gradPlusFee / 100))
  }

  if (sFee < 0) {
    sFee = 0
  }

  if (gFee < 0) {
    gFee = 0
  }

  return {
    stafford: sFee,
    gradplus: gFee,
  }
}

function number_format(num) {
  return num ? `$${num.toLocaleString()}` : "-"
}

const yearPrefixes = computed(() =>
  (partTime.value ? ["y1_", "y2_", "y3_", "y4_", "y5_"] : ["y1_", "y2_", "y3_"]),
)
</script>
